// Auto-injected by the Supabase integration when this file does not exist.
//
// Pathless layout route that gates every child under `src/routes/_authenticated/`
// behind a signed-in Supabase user. The subtree is client-rendered (`ssr: false`)
// because Supabase stores the session in `localStorage`, which the server cannot
// read.
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { supabase } from '@/integrations/supabase/client'
import { pingPresence } from '@/lib/presence.functions'

const SIGN_IN_ROUTE = '/login'

export const Route = createFileRoute('/_authenticated')({
  ssr: false,
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session?.user) {
      throw redirect({ to: SIGN_IN_ROUTE })
    }
    return { user: data.session.user }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const ping = useServerFn(pingPresence)
  useEffect(() => {
    // Fire immediately + every 60s while the tab is open & visible.
    let cancelled = false
    const beat = () => {
      if (cancelled) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      ping({}).catch(() => {})
    }
    beat()
    const id = window.setInterval(beat, 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') beat() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelled = true
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [ping])
  return <Outlet />
}
